import { WeatherResponse } from '@/types';

import { parseWallClock } from './wallClock';

export const isValidPhoneNumber = (value: string) => {
  if (value) {
    const phoneNumber = value.replace(/[-()]/g, '');
    return phoneNumber?.length === 10 || phoneNumber.length === 11;
  }
};

export function isPostalCode(value: string) {
  const postalCodeRegex = /^\d{5}(-\d{4})?$/;
  return postalCodeRegex.test(value);
}

export function isDate(value: Date) {
  return value instanceof Date && !Number.isNaN(value.getTime());
}

export function isUUID(value: string) {
  return value.match(/^[a-z,0-9,-]{36,36}$/i) !== null;
}

export function chunk<T>(list: T[], size: number): T[][] {
  const chunks = Math.ceil(list.length / size);
  return Array.from({ length: chunks }, (_, i) =>
    list.slice(i * size, i * size + size),
  );
}

export function mergeObjects<T extends object>(objects: T[]): Partial<T> {
  if (objects.length === 0) {
    return {};
  }

  const result: Partial<T> = {};
  const keys = Object.keys(objects[0]) as (keyof T)[];

  for (const key of keys) {
    const values = objects.map((obj) => obj[key]);

    if (values.every((v) => v && typeof v === 'object' && !Array.isArray(v))) {
      result[key] = mergeObjects(values as object[]) as T[keyof T];
    } else {
      const allEqual = values.every((v) => v === values[0]);
      result[key] = allEqual ? values[0] : undefined;
    }
  }

  return result;
}

export function mergeDonorScheduleObjects<T extends object>(
  objects: T[],
): Partial<T> {
  if (objects.length === 0) {
    return {};
  }

  const result: Partial<T> = {};
  const keys = Object.keys(objects[0]) as (keyof T)[];

  for (const key of keys) {
    const values = objects.map((obj) => obj[key]);

    if (values.every((v) => v && typeof v === 'object' && !Array.isArray(v))) {
      result[key] = mergeDonorScheduleObjects(values as object[]) as T[keyof T];
    } else {
      if (key === 'date' && values.every((v) => v && typeof v === 'string')) {
        const dates = values.map((v) => new Date(v as string));
        const allSameDate = dates.every(
          (date) =>
            date.getFullYear() === dates[0].getFullYear() &&
            date.getMonth() === dates[0].getMonth() &&
            date.getDate() === dates[0].getDate(),
        );
        result[key] = allSameDate ? values[0] : undefined;
      } else {
        const allEqual = values.every((v) => v === values[0]);
        result[key] = allEqual ? values[0] : undefined;
      }
    }
  }

  return result;
}

export function diffObjects<T extends object>(obj1: T, obj2: T): Partial<T> {
  const result: Partial<T> = {};
  const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]) as Set<
    keyof T
  >;

  for (const key of keys) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    if (
      (val1 === undefined || val1 === null) &&
      (val2 === undefined || val2 === null)
    ) {
      continue;
    }

    if (
      typeof val1 === 'object' &&
      val1 !== null &&
      !Array.isArray(val1) &&
      typeof val2 === 'object' &&
      val2 !== null &&
      !Array.isArray(val2)
    ) {
      const nestedDiff = diffObjects(val1 as object, val2 as object);
      if (Object.keys(nestedDiff).length > 0) {
        result[key] = nestedDiff as T[keyof T];
      }
    } else if (val1 !== val2) {
      result[key] = { old: val1, new: val2 } as any;
    }
  }

  return result;
}

export function getDifferencesOnly<T extends object>(
  obj1: T,
  obj2: T,
): Partial<T> {
  const result: Partial<T> = {};
  const keys = new Set([...Object.keys(obj1), ...Object.keys(obj2)]) as Set<
    keyof T
  >;

  for (const key of keys) {
    const val1 = obj1[key];
    const val2 = obj2[key];

    if (
      (val1 === undefined || val1 === null) &&
      (val2 === undefined || val2 === null)
    ) {
      continue;
    }

    if (
      typeof val1 === 'object' &&
      val1 !== null &&
      !Array.isArray(val1) &&
      typeof val2 === 'object' &&
      val2 !== null &&
      !Array.isArray(val2)
    ) {
      const nestedDiff = getDifferencesOnly(val1 as object, val2 as object);
      if (Object.keys(nestedDiff).length > 0) {
        result[key] = nestedDiff as T[keyof T];
      }
    } else if (val1 !== val2) {
      result[key] = val2;
    }
  }

  return result;
}

export const fetchWeather = async () => {
  const position = await new Promise<GeolocationPosition>((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject);
  });

  const { latitude, longitude } = position.coords;

  const uri = `${import.meta.env.VITE_WEATHER_API_URL}${latitude},${longitude}?unitGroup=metric&key=${import.meta.env.VITE_WEATHER_API_KEY}&contentType=json`;

  const response = await fetch(uri);
  const data: WeatherResponse = await response.json();
  return data;
};

export const isGuid = (value: any) =>
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
    value,
  );

export const incrementRevision = (revision: string | number): string => {
  if (typeof revision === 'number') {
    return String(revision + 1);
  }

  const numericValue = parseInt(revision, 10);
  if (!Number.isNaN(numericValue)) {
    return String(numericValue + 1);
  }

  return revision + '1';
};

export const incrementCurrentRevision = (
  previousRevision: string,
  currentRevision: string,
  topRevisions?: Array<{
    revisionNumber: string | null;
    fertilizationSiteId: string | null;
    fertilizationSiteName: string | null;
  }>,
  fertilizationSiteId?: string,
): { previousRevision: string; currentRevision: string } => {
  // Helper function to get the highest revision number from top revisions for the current site
  const getHighestTopRevision = (): string | null => {
    if (!topRevisions || !fertilizationSiteId) return null;

    const siteRevisions = topRevisions
      .filter((rev) => rev.fertilizationSiteId === fertilizationSiteId)
      .map((rev) => rev.revisionNumber)
      .filter((rev) => rev !== null) as string[];

    if (siteRevisions.length === 0) return null;

    // Find the highest numeric revision
    let highestNumeric = 0;
    let highestRevision = siteRevisions[0];

    for (const rev of siteRevisions) {
      const numericValue = parseInt(rev, 10);
      if (!Number.isNaN(numericValue) && numericValue > highestNumeric) {
        highestNumeric = numericValue;
        highestRevision = rev;
      }
    }

    return highestRevision;
  };

  // Get the highest revision from top revisions
  const highestTopRevision = getHighestTopRevision();

  // Helper function to get the next revision number
  const getNextRevision = (baseRevision: string): string => {
    if (highestTopRevision) {
      const highestNumeric = parseInt(highestTopRevision, 10);
      const baseNumeric = parseInt(baseRevision, 10);

      // If the highest top revision is higher than our base, use that as starting point
      if (
        !Number.isNaN(highestNumeric) &&
        !Number.isNaN(baseNumeric) &&
        highestNumeric > baseNumeric
      ) {
        return String(highestNumeric + 1);
      }
    }

    return incrementRevision(baseRevision);
  };

  if (!currentRevision) {
    const previousRevisions = previousRevision.split(', ').map((r) => r.trim());
    const lastPreviousRevision =
      previousRevisions[previousRevisions.length - 1];
    const newCurrent = getNextRevision(lastPreviousRevision);
    return {
      previousRevision,
      currentRevision: newCurrent,
    };
  }

  if (currentRevision === previousRevision) {
    const previousRevisions = previousRevision.split(', ').map((r) => r.trim());
    const lastPreviousRevision =
      previousRevisions[previousRevisions.length - 1];
    const newCurrent = `${currentRevision}, ${getNextRevision(lastPreviousRevision)}`;
    return {
      previousRevision,
      currentRevision: newCurrent,
    };
  }

  const currentRevisions = currentRevision.split(', ').map((r) => r.trim());
  const lastRevision = currentRevisions[currentRevisions.length - 1];
  const newRevision = getNextRevision(lastRevision);
  const newCurrent = [...currentRevisions, newRevision].join(', ');

  return {
    previousRevision,
    currentRevision: newCurrent,
  };
};

export function enumToOptions<T extends string | number>(
  enumObj: Record<string, T>,
  parseValue = false,
): Array<{ label: string; value: T }> {
  return Object.entries(enumObj).map(([label, value]) => ({
    label,
    value:
      parseValue && typeof value === 'string'
        ? (parseInt(value, 10) as T)
        : value,
  }));
}

export function mapToOptions(
  mapObj: Record<string, string>,
  parseKey = false,
): Array<{ label: string; value: number | string }> {
  return Object.entries(mapObj).map(([value, label]) => ({
    label,
    value: parseKey ? parseInt(value, 10) : value,
  }));
}

export const transformCheckboxValue = (
  value: boolean | number | undefined | null,
): number | null | boolean => {
  if (value === undefined || value === null) {
    return null;
  }
  if (typeof value === 'number') {
    return value;
  }
  return value ? true : false;
};

export const toDateOrNull = (value: string | null | undefined) =>
  parseWallClock(value);

export const toIdNameObject = (
  id: string | null | undefined,
  name: string | null | undefined,
) => (id && name ? { id, name } : id);
